// Product form.
//
// What our sections need from a form and nothing else. Two halves, and both
// are about this one form, which is why they are one file:
//
//   <nextdev-product-form>  posts the line, says what the cart refused, opens
//                           the drawer
//   nextdevCartCombine      puts the extra lines a snippet wants in beside it,
//                           and ties them to it
//
// In the theme:
//
//   assets/nextdev-product-form.js
//
// It registers <nextdev-product-form>, not <product-form>. A custom element
// name can only be defined once, so a file that took the theme's name would
// either lose to it or silently replace it, whichever loaded first. A name of
// our own can never fight with anything the theme has.
//
// It came from the theme's own product-form.js, which had grown thirteen
// things on top of the post: an upsell modal with a countdown, a checkout
// upsell, a second checkout button, a sticky bar, a two step modal on a phone,
// upsell bundling with bundle properties, a second set, a blanket upsell, a
// bundle prompt, a swiper, and z-index juggling for the modals. Every one of
// them belonged to a section of the old theme. None is here.
//
// Three things a theme may or may not have are read, each guarded, so this
// works with or without them: the cart drawer adapter, window.routes, and the
// cart count hook. With no drawer the form lands on /cart, the way a form with
// no script at all would.
//
// The drawer this talks to is nextdev-cart-drawer. Everything it asks of it,
// getSectionsToRender, setActiveElement, renderContents and the is-empty class,
// the drawer has, and the adapter it hands back is a proxy onto whatever
// element is live at the time.
//
// The names it looks up, product-form__error-message-wrapper, sold-out-message
// and loading-overlay__spinner, are the theme's own and stay as the theme
// writes them, because the theme's css is what draws them.

if (!customElements.get('nextdev-product-form')) {
  customElements.define(
    'nextdev-product-form',
    class NextDevProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        // Liquid writes the variant id disabled, so a page whose script never
        // arrives posts nothing rather than posting a line with no variant.
        // Reaching this far means the script did arrive.
        var id = this.form.querySelector('[name="id"]');
        if (id) id.disabled = false;

        this.submitButton = this.querySelector('[type="submit"]');
        if (this.cart && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      // Asked for every time rather than kept from the constructor. An element
      // is upgraded the moment its definition lands, and a drawer rendered
      // after that, by a section the cart replaced or an app, would never be
      // found again. The drawer's own adapter is a proxy onto whatever element
      // is live, so asking again costs nothing.
      get cart() {
        return (typeof getNextDevCartAdapter === 'function' && getNextDevCartAdapter()) || null;
      }

      get addUrl() {
        return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      }

      get cartUrl() {
        return (window.routes && window.routes.cart_url) || '/cart';
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.say();
        this.busy(true);

        // Whatever the form carries is what goes in, a lone id or the
        // items[n][id] fields something of ours wrote for more than one line.
        var body = new FormData(this.form);

        var cart = this.cart;

        // what the drawer needs back to redraw itself in the same request
        if (cart) {
          body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
          body.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        var self = this;
        fetch(this.addUrl, {
          method: 'POST',
          // no Content-Type: the browser writes the multipart boundary itself
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: body
        })
          .then(function (response) { return response.json(); })
          .then(function (answer) {
            // the cart answers a refusal with a status and takes a line without one
            if (answer.status) {
              self.failed = true;
              self.say(answer.description);
              self.soldOut();
              return;
            }

            self.failed = false;
            if (typeof checkCartItems === 'function') checkCartItems();

            if (!cart) {
              window.location = self.cartUrl;
              return;
            }
            cart.renderContents(answer);
          })
          .catch(function (e) { console.error(e); })
          .finally(function () { self.busy(false); });
      }

      // The spinner going from shown to hidden is how anything else on the page
      // knows the post is over, the floating bar above all, so it is toggled
      // around the whole request and nothing else.
      busy(on) {
        this.submitButton.classList.toggle('loading', on);
        if (on) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else if (!this.failed) {
          this.submitButton.removeAttribute('aria-disabled');
        }

        var spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);

        var cart = this.cart;
        if (!on && cart && cart.classList && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      }

      // the label and the sold out line swap places, the way the picker does it
      soldOut() {
        var message = this.submitButton.querySelector('.sold-out-message');
        if (!message) return;
        var label = this.submitButton.querySelector('span');
        if (label) label.classList.add('hidden');
        message.classList.remove('hidden');
      }

      say(text) {
        var wrap = this.querySelector('.product-form__error-message-wrapper');
        if (!wrap) return;
        wrap.toggleAttribute('hidden', !text);
        if (!text) return;
        var line = wrap.querySelector('.product-form__error-message');
        if (line) line.textContent = text;
      }
    }
  );
}

// ---------------------------------------------------------------------------
// The extra lines.
//
// A snippet that wants a line added beside the form's own registers what it
// would add:
//
//   window.nextdevCartCombine.register(form, function (form) {
//     return [{ id: 123, quantity: 1 }];   // or nothing at all
//   });
//
// On submit it posts every registered line in one request, ties them to the
// form's own line, and lets the submit go. With nothing registered, or with
// everything saying no, the submit is not touched at all.
//
// Why one place rather than a few lines in each snippet. Both the free gift
// picker and the upsell card used to catch the form's submit themselves, in
// the capture phase on the document, each holding a flag so it would let its
// own resubmit through. Two of them on one page never stopped: a listener on
// the same node is not stopped by stopPropagation, so each saw the other's
// resubmit as a fresh submit, added its line again and resubmitted. Measured
// at 148 adds each and not one bed in the cart. One listener per form cannot
// do that to itself.
//
// The tie is a pair of line item properties, the same group on both sides:
// _nextdev_bundle on the form's own line, _nextdev_bundle_with on every line
// added beside it. Both start with an underscore, so the customer never sees
// them, and the cart drawer reads them to draw the extra lines under the one
// they came with.
// ---------------------------------------------------------------------------

(function () {
  'use strict';

  if (window.nextdevCartCombine) return;

  var GROUP_FIELD = 'properties[_nextdev_bundle]';
  var CHILD_KEY = '_nextdev_bundle_with';

  var forms = [];

  function entryFor(form) {
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].form === form) return forms[i];
    }
    var entry = { form: form, sources: [], passing: false, busy: false };
    forms.push(entry);
    install(entry);
    return entry;
  }

  // One group per add, never per product: Shopify merges two lines only when
  // their properties match, so a second bed added later would otherwise fold
  // its gift into the first one's line.
  function newGroup() {
    return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  // The form carries the group on its own line. The field is made here rather
  // than in each snippet's markup, so two snippets on one page cannot write two
  // fields of the same name into the same form.
  function groupField(form) {
    var field = form.querySelector('[data-nextdev-combine-group]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = GROUP_FIELD;
      field.setAttribute('data-nextdev-combine-group', '');
      form.appendChild(field);
    }
    return field;
  }

  function collect(entry) {
    var lines = [];
    entry.sources.forEach(function (source) {
      var got;
      try {
        got = source(entry.form);
      } catch (e) {
        return;
      }
      if (!got) return;
      (got.length === undefined ? [got] : got).forEach(function (line) {
        if (line && line.id) {
          lines.push({ id: Number(line.id), quantity: line.quantity > 0 ? line.quantity : 1 });
        }
      });
    });
    return lines;
  }

  function addUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  // A form will not take a second submit inside the same task as the first, so
  // the resubmit goes in a fresh one.
  function resubmit(entry, submitter) {
    window.setTimeout(function () {
      entry.passing = true;
      if (entry.form.requestSubmit) {
        entry.form.requestSubmit(submitter || undefined);
      } else {
        entry.form.submit();
      }
      entry.passing = false;
      // the form's own half has it from here, and its button is what refuses
      // the next click until the post is over
      entry.busy = false;
    }, 0);
  }

  function install(entry) {
    // The capture phase on the document runs before anything bound to the form
    // itself, so the theme never sees this submit until the extra lines are in.
    document.addEventListener('submit', function (event) {
      if (event.target !== entry.form || entry.passing) return;

      // A second click while the first add is still in the air. The form's own
      // half turns the button to aria-disabled and refuses the submit, but that
      // only happens once the extra lines are in and the form has been handed
      // the submit back, so between the two this half is the only thing holding
      // the door. Without it the gift went in twice, under two groups, leaving
      // a line in the cart with no bed to belong to.
      var button = entry.form.querySelector('[type="submit"]');
      if (entry.busy || (button && button.getAttribute('aria-disabled') === 'true')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      var lines = collect(entry);
      // nothing to combine, so the form is left exactly as it was: no group is
      // written and the submit is not touched
      if (!lines.length) return;

      event.preventDefault();
      event.stopPropagation();
      entry.busy = true;
      var submitter = event.submitter || entry.form.querySelector('[type="submit"]');

      var group = newGroup();
      groupField(entry.form).value = group;

      lines.forEach(function (line) {
        line.properties = {};
        line.properties[CHILD_KEY] = group;
      });

      // a line the cart will not take is not worth losing the sale over, so the
      // form goes either way
      fetch(addUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines })
      })
        .catch(function () { /* falls through */ })
        .then(function () { resubmit(entry, submitter); });
    }, true);
  }

  window.nextdevCartCombine = {
    register: function (form, source) {
      if (!form || typeof source !== 'function') return;
      entryFor(form).sources.push(source);
    }
  };
})();